import type { Option } from '@/lib/format';

/** A filter definition; `key` is the query parameter name sent to the API (`stage`, `text`, `openOnly`). */
export type FilterDef =
  | { key: string; label: string; type: 'enum'; options: ReadonlyArray<Option>; placeholder?: string }
  | { key: string; label: string; type: 'text'; placeholder?: string }
  | { key: string; label: string; type: 'boolean' };

export type FilterValues = Record<string, string | string[] | boolean | undefined>;

/** Number of active filters (for badges). */
export function countActiveFilters(values: FilterValues): number {
  return Object.values(values).filter((v) =>
    Array.isArray(v) ? v.length > 0 : v !== undefined && v !== '' && v !== false,
  ).length;
}

/** Human summary of one filter value, for chips. */
export function describeFilter(def: FilterDef | undefined, value: FilterValues[string]): string | null {
  if (value === undefined || value === '' || value === false) return null;
  if (!def) return Array.isArray(value) ? value.join(', ') : String(value);
  if (def.type === 'enum') {
    const vals = Array.isArray(value) ? value : [String(value)];
    if (vals.length === 0) return null;
    const labels = vals.map((v) => def.options.find((o) => o.value === v)?.label ?? v);
    return `${def.label}: ${labels.join(', ')}`;
  }
  if (def.type === 'boolean') return def.label;
  return `${def.label}: ${String(value)}`;
}

/** Drop empty values so the object can be sent as query params or stored in a view. */
export function cleanFilters(values: FilterValues): FilterValues {
  const out: FilterValues = {};
  for (const [k, v] of Object.entries(values)) {
    if (v === undefined || v === '' || v === false) continue;
    if (Array.isArray(v) && v.length === 0) continue;
    out[k] = v;
  }
  return out;
}
